import {
  Group,
  Text,
  Tooltip,
  useMantineTheme,
} from '@mantine/core'

import {
  Ban,
  BatteryCharging,
  BatteryFull,
  BatteryLow,
  BatteryMedium,
  BatteryWarning,
} from 'lucide-react'

import { DeviceBase } from '@data/device/model/device'
import { HType } from '@data/tickets/model/ticket'

interface BattIconProps {
  device: DeviceBase
}

interface SimpleBattIconProps {
  batteryLevel : number | null | undefined
  deviceType: HType
}

const getBatteryPercentage = (
  batteryLevel: number,
  vendor: string,
) => {
  let battPct = batteryLevel

  switch (vendor) {
    case 'SMARTFLOW': {
      const maxV = 7000

      const vBatt =
        batteryLevel > maxV
          ? 7000
          : batteryLevel

      const maxVDelta = 2000

      const vDelta =
        maxVDelta -
        (maxV - vBatt)

      battPct = Math.round(
        (vDelta < 0
          ? 0
          : vDelta / maxVDelta) *
        100,
      )

      return battPct
    }

    case 'METASPHERE': {
      const maxV = 7010

      const vBatt =
        batteryLevel > maxV
          ? 7010
          : batteryLevel

      const maxVDelta = 1500

      const vDelta =
        maxVDelta -
        (maxV - vBatt)

      battPct = Math.round(
        (vDelta / maxVDelta) * 100,
      )

      return battPct
    }

    default:
      return battPct
  }
}

const getBatteryPercentageDeviceType = (
  batteryLevel: number,
  vendor: string,
) => {
  let battPct = batteryLevel

  switch (vendor) {
    case '3V004': {
      const maxV = 7000

      const vBatt =
        batteryLevel > maxV
          ? 7000
          : batteryLevel

      const maxVDelta = 2000

      const vDelta =
        maxVDelta -
        (maxV - vBatt)

      battPct = Math.round(
        (vDelta < 0
          ? 0
          : vDelta / maxVDelta) *
        100,
      )

      return battPct
    }

    case 'POINT GREEN 3G': {
      const maxV = 7010

      const vBatt =
        batteryLevel > maxV
          ? 7010
          : batteryLevel

      const maxVDelta = 1500

      const vDelta =
        maxVDelta -
        (maxV - vBatt)

      battPct = Math.round(
        (vDelta / maxVDelta) * 100,
      )

      return battPct
    }

    default:
      return battPct
  }
}

// The bands this icon renders, strongest first. Exported so the Battery column's filter offers
// exactly what the icon can display, and used as the label type below so adding a band here
// without adding it to the filter is a compile error.
export const BATTERY_BAND_VALUES = [
  'Excellent',
  'Good',
  'Medium',
  'Low',
  'Critical',
] as const

export type BatteryBand = (typeof BATTERY_BAND_VALUES)[number]

// Every band this device visibly shows. BattIcon renders an internal reading and, when the
// device reports one, an external reading beside it — so a device can display two bands at
// once and the filter has to match on either. An absent reading shows a ban icon and yields no
// bands, which means it matches no selection.
export const getBatteryBands = (device: DeviceBase): BatteryBand[] => {
  if (device.batteryLevel === null || device.batteryLevel === undefined) return []

  const bands: BatteryBand[] = [
    getBatteryDisplay(getBatteryPercentage(device.batteryLevel, device.deviceVendor)).label,
  ]

  if (device.batteryLevelExt !== null && device.batteryLevelExt !== undefined) {
    bands.push(
      getBatteryDisplay(getBatteryPercentage(device.batteryLevelExt, device.deviceVendor)).label,
    )
  }

  return bands
}

const getBatteryDisplay = (
  percentage: number,
): {
  color: string
  icon: typeof BatteryFull
  label: BatteryBand
} => {
  if (percentage >= 90) {
    return {
      color: 'green',
      icon: BatteryFull,
      label: 'Excellent',
    }
  }

  if (percentage >= 75) {
    return {
      color: 'lime',
      icon: BatteryCharging,
      label: 'Good',
    }
  }

  if (percentage >= 50) {
    return {
      color: 'yellow',
      icon: BatteryMedium,
      label: 'Medium',
    }
  }

  if (percentage >= 25) {
    return {
      color: 'orange',
      icon: BatteryLow,
      label: 'Low',
    }
  }

  return {
    color: 'red',
    icon: BatteryWarning,
    label: 'Critical',
  }
}

interface BatteryIconProps {
  label: string
  percentage: number
}

const BatteryIcon = ({
                       label,
                       percentage,
                     }: BatteryIconProps) => {
  const theme = useMantineTheme()

  const battery =
    getBatteryDisplay(
      percentage,
    )

  const Icon = battery.icon

  return (
    <Tooltip
      withArrow
      label={`${label} Battery: ${percentage}% (${battery.label})`}
    >
      <Group gap={4} wrap="nowrap">
        <Icon
          size={30}
          color={
            theme.colors[
              battery.color
              ][6]
          }
        />

        <Text
          size="sm"
          fw={500}
          c={battery.color}
        >
          {percentage}%
        </Text>
      </Group>
    </Tooltip>
  )
}

export const BattIcon = ({ device }: BattIconProps) => {
  const theme = useMantineTheme()

  if (
    device.batteryLevel ===
    null ||
    device.batteryLevel ===
    undefined
  ) {
    return (
      <Tooltip label="Battery unavailable">
        <Ban
          size={18}
          color={theme.colors.gray[6]}
        />
      </Tooltip>
    )
  }

  const internalBattery =
    getBatteryPercentage(
      device.batteryLevel,
      device.deviceVendor,
    )

  const externalBattery =
    device.batteryLevelExt !==
    null &&
    device.batteryLevelExt !==
    undefined
      ? getBatteryPercentage(
        device.batteryLevelExt,
        device.deviceVendor,
      )
      : null

  return (
    <Group gap="sm">
      <BatteryIcon
        label="Internal"
        percentage={internalBattery}
      />

      {externalBattery !==
        null && (
          <BatteryIcon
            label="External"
            percentage={externalBattery}
          />
        )}
    </Group>
  )
}

export const SimpleBattIcon = ({ batteryLevel, deviceType } : SimpleBattIconProps ) => {
  const theme = useMantineTheme()

  if (
    batteryLevel ===
    null ||
    batteryLevel ===
    undefined
  ) {
    return (
      <Tooltip label="Battery unavailable">
        <Ban
          size={18}
          color={theme.colors.gray[6]}
        />
      </Tooltip>
    )
  }

  const internalBattery = getBatteryPercentageDeviceType(batteryLevel, deviceType)

  return (
    <Group gap="sm">
      <BatteryIcon
        label="Internal"
        percentage={internalBattery}
      />
    </Group>
  )
}